function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import pick from 'lodash/pick';
import Validator from 'libs/Validator';
import FormContainerAbstract from 'components/FormContainerAbstract';
import Form from 'components/Form';
import FormGroup from 'components/Form/FormGroup';
import AutosuggestInput from 'components/Form/AutosuggestInput';
import Button from 'components/Form/Button';
import App from 'modules/App';
import Information from 'modules/Information';
import * as actions from '../../../../actions';
import * as constants from '../../../../constants';
import * as selectors from '../../../../selectors';
import * as shapes from '../../../../shapes';
import messages from '../../../../messages';
class PatientProfileFrom extends FormContainerAbstract {
    componentDidUpdate(prevProps) {
        const { isInProgress, hasErrors } = this.props;
        if (prevProps.isInProgress !== isInProgress && !isInProgress && !hasErrors) {
            this.onCommit();
        }
    }
    onPopulate() {
        if (!this.props.patientProfile) {
            return;
        }
        const { patientProfile } = this.props;
        const values = pick(patientProfile, [
            'payer'
        ]);
        this.onSetValues(values);
    }
    onValidate(rules) {
        const values = _object_spread({}, get(this.props.formValues, 'values', {}));
        const clonedRules = _object_spread({}, rules);
        clonedRules.payer = this.isPayerMandatory ? 'required' : 'trim';
        return Validator.run(values, clonedRules);
    }
    onSubmit() {
        this.props.onFormProcessing();
        const { validatedValues, errors } = this.onValidate(this.validatorRules);
        this.props.onFormErrors(errors);
        if (!errors) {
            const { payer } = validatedValues;
            this.props.onSubmit(validatedValues);
            if (payer) {
                const { countryId } = this.props.account;
                this.props.onAddPayerProposal(countryId, payer);
            }
        }
    }
    get isPayerMandatory() {
        const { countrySettings } = this.props;
        return countrySettings ? countrySettings.isPayerMandatory : false;
    }
    renderCancelBtn() {
        return /*#__PURE__*/ _jsx(Button, {
            styleModifier: "primary",
            className: "btn--block",
            labelMessage: App.messages.buttons.discardChanges,
            isDisabled: this.isClean,
            onClick: ()=>this.onReset()
        });
    }
    renderSubmitBtn() {
        return /*#__PURE__*/ _jsx(Button, {
            type: "submit",
            styleModifier: "primary",
            labelMessage: App.messages.buttons.saveChanges,
            className: "btn--block btn--filled",
            isDisabled: this.isClean,
            isInProgress: this.props.isInProgress
        });
    }
    renderActions() {
        return /*#__PURE__*/ _jsxs("div", {
            className: "row",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "col-6",
                    children: this.renderCancelBtn()
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "col-6",
                    children: this.renderSubmitBtn()
                })
            ]
        });
    }
    // TODO: What about Personal Identifier
    renderPersonalIdentifier() {
        const personalIdentifier = get(this.props.information, 'personalIdentifier');
        if (!personalIdentifier) {
            return null;
        }
        const { personalIdentifierType } = personalIdentifier;
        const label = get(this.props.localizationResources, [
            personalIdentifierType,
            'value'
        ], personalIdentifierType);
        return /*#__PURE__*/ _jsxs("div", {
            className: "form-group",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "form-label",
                    children: label
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text--large",
                    children: personalIdentifier.personalIdentifierValue
                })
            ]
        });
    }
    renderForm() {
        return /*#__PURE__*/ _jsxs(Form, {
            onSubmit: ()=>this.onSubmit(),
            children: [
                this.renderPersonalIdentifier(),
                /*#__PURE__*/ _jsx(FormGroup, {
                    id: "payer",
                    labelMessage: messages.labels.payer,
                    formValues: this.props.formValues,
                    children: /*#__PURE__*/ _jsx(AutosuggestInput, {
                        suggestions: this.props.payers,
                        placeholder: messages.placeholders.payer,
                        onChange: (input)=>this.onSetValue(input)
                    })
                }),
                this.renderActions()
            ]
        });
    }
    render() {
        return /*#__PURE__*/ _jsxs("section", {
            children: [
                /*#__PURE__*/ _jsx("h2", {
                    className: "section__header",
                    children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.headers.patientProfile))
                }),
                this.renderForm()
            ]
        });
    }
    constructor(props){
        super(props);
        props.onFetchPayers();
        this.onPopulate();
    }
}
_define_property(PatientProfileFrom, "propTypes", {
    // Explicit props
    patientProfile: PropTypes.object.isRequired,
    // Implicit props
    account: shapes.account,
    payers: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired
    })),
    information: PropTypes.object,
    localizationResources: PropTypes.object,
    // Implicit actions
    onFetchPayers: PropTypes.func
});
const patientProfileFormSelector = App.selectors.formSelector(constants.PATIENT_PROFILE_FORM);
const mapStateToProps = (state)=>({
        account: selectors.account(state),
        payers: selectors.payers(state),
        isInProgress: selectors.isUpdatePatientProfileInProgress(state),
        hasErrors: selectors.hasUpdatePatientProfileErrors(state),
        information: Information.selectors.information(state),
        formValues: patientProfileFormSelector(state),
        localizationResources: App.selectors.localizationResources(state)
    });
const mapDispatchToProps = (dispatch)=>{
    const formName = constants.PATIENT_PROFILE_FORM;
    return {
        onAddPayerProposal: (countryId, payer)=>dispatch(App.actions.addPayerProposal(countryId, payer)),
        onFetchPayers: ()=>dispatch(actions.fetchPayers()),
        onSubmit: (values)=>dispatch(actions.updatePatientProfile(values)),
        onSetFormValue: (input)=>dispatch(App.actions.setFormValue(formName, input)),
        onSetFormValues: (values)=>dispatch(App.actions.setFormValues(formName, values)),
        onFormErrors: (errors)=>dispatch(App.actions.setFormErrors(formName, errors)),
        onFormProcessing: ()=>dispatch(App.actions.startFormProcessing(formName)),
        onClearForm: ()=>dispatch(App.actions.clearForm(formName)),
        onCommitForm: ()=>dispatch(App.actions.commitForm(formName)),
        onResetForm: ()=>dispatch(App.actions.resetForm(formName))
    };
};
const ConnectedPatientProfileForm = connect(mapStateToProps, mapDispatchToProps)(PatientProfileFrom);
export default ConnectedPatientProfileForm;
